/**
 * Goal 2: send_report as one dialog-owned operation (queue + poll).
 * Dialog owns feedback — no toast-per-poll / queue success toast.
 * Queued/202 stays RUNNING until poll confirms completed (FA-04).
 */
import {
  getOperationCoordinator,
  STEP_STATUS,
  OUTCOME,
  normalizeError,
  hasMeaningfulErrors,
  classifyHttpSuccess,
  canConfirmStepFromEvidence,
  outcomeAfterRefreshFailure,
} from "../../../../operationFeedback";
import {
  sendReport,
  checkSendReportStatus,
} from "../../../../api/service/report";

export const SEND_REPORT_OPERATION_KEY = "send_report";

export const DEFAULT_SEND_POLL_MAX_ATTEMPTS = 60;
export const DEFAULT_SEND_POLL_INTERVAL_MS = 5000;

/**
 * @param {object} values
 * @returns {{ ready: boolean, reason: string|null }}
 */
export function evaluateSendReportPreflight(values) {
  if (!values?.id) {
    return { ready: false, reason: "missing_id" };
  }
  const email = String(values?.email || "").trim();
  if (!email) {
    return { ready: false, reason: "missing_email" };
  }
  if (!values?.printReportType) {
    return { ready: false, reason: "missing_report_type" };
  }
  return { ready: true, reason: null };
}

function defaultExecutionSteps(t) {
  return [
    {
      key: "preflight",
      label: t("operation_feedback.send_report.step.preflight"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "queue",
      label: t("operation_feedback.send_report.step.queue"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "deliver",
      label: t("operation_feedback.send_report.step.deliver"),
      status: STEP_STATUS.PENDING,
    },
    {
      key: "refresh",
      label: t("operation_feedback.send_report.step.refresh"),
      status: STEP_STATUS.PENDING,
    },
  ];
}

function preflightDetail(reason, t) {
  if (reason === "missing_email") {
    return t("operation_feedback.send_report.preflight_missing_email");
  }
  if (reason === "missing_report_type") {
    return t("operation_feedback.send_report.preflight_missing_type");
  }
  return t("operation_feedback.send_report.preflight_missing_id");
}

function extractJobId(payload) {
  return (
    payload?.job_id ||
    payload?.data?.job_id ||
    payload?.jobId ||
    payload?.data?.jobId ||
    null
  );
}

function isQueueAccepted(response) {
  const status = response?.status;
  const classified = classifyHttpSuccess(status, response?.data);
  if (status === 202) return true;
  if (classified.ok || status === 200) {
    return Boolean(extractJobId(response?.data) || response?.data?.status === 200);
  }
  return false;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * @param {object} args
 * @param {object} args.values - send form payload (id, email, name, printReportType, …)
 * @param {object} [args.report]
 * @param {string|number} [args.companyId]
 * @param {Function} [args.onRefresh]
 * @param {Function} args.t
 * @param {Function} [args.sendFn] - injectable (tests)
 * @param {Function} [args.checkStatusFn] - injectable (tests)
 * @param {Function} [args.sleepFn] - injectable (tests)
 * @param {number} [args.maxAttempts]
 * @param {number} [args.pollIntervalMs]
 */
export async function runSendReportOperation({
  values,
  report,
  companyId,
  onRefresh,
  t,
  sendFn = sendReport,
  checkStatusFn = checkSendReportStatus,
  sleepFn = sleep,
  maxAttempts = DEFAULT_SEND_POLL_MAX_ATTEMPTS,
  pollIntervalMs = DEFAULT_SEND_POLL_INTERVAL_MS,
}) {
  const coordinator = getOperationCoordinator();
  const resourceId = values?.id || report?.id;
  const title = t("operation_feedback.send_report.title", {
    number: report?.report_number || report?.displayTitle || resourceId || "",
  });

  const op = coordinator.start({
    operationKey: SEND_REPORT_OPERATION_KEY,
    title,
    presentationOwner: "dialog",
    resourceId,
    companyId,
    steps: defaultExecutionSteps(t),
  });
  const id = op.clientOperationId;
  let rev = 1;

  const preflight = evaluateSendReportPreflight(values);

  // Readiness must never count as confirmed execution success.
  if (
    !canConfirmStepFromEvidence({
      fromReadiness: true,
      status: STEP_STATUS.COMPLETED,
    })
  ) {
    /* intentional gate */
  }

  if (!preflight.ready) {
    const detail = preflightDetail(preflight.reason, t);
    coordinator.updateStep(id, "preflight", {
      status: STEP_STATUS.NEEDS_FIX,
      detail,
      revision: rev++,
    });
    coordinator.updateStep(id, "queue", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "deliver", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_RELEVANT,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: detail,
      outcome: OUTCOME.NOT_APPLIED,
    });
    return { clientOperationId: id, ok: false, error: { kind: "preflight" } };
  }

  coordinator.updateStep(id, "preflight", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.send_report.preflight_ok"),
    revision: rev++,
  });

  coordinator.updateStep(id, "queue", {
    status: STEP_STATUS.RUNNING,
    revision: rev++,
  });

  let response;
  try {
    response = await sendFn(values);
  } catch (err) {
    const normalized = normalizeError(err, {
      operationKey: SEND_REPORT_OPERATION_KEY,
    });
    coordinator.updateStep(id, "queue", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "deliver", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized };
  }

  const payload = response?.data || {};
  if (
    response?.status >= 400 ||
    hasMeaningfulErrors(payload?.errors) ||
    payload?.error === true ||
    !isQueueAccepted(response)
  ) {
    const normalized = normalizeError(
      { response: { status: response?.status || 422, data: payload } },
      { operationKey: SEND_REPORT_OPERATION_KEY }
    );
    coordinator.updateStep(id, "queue", {
      status: STEP_STATUS.FAILED,
      detail: normalized.safeMessage,
      revision: rev++,
    });
    coordinator.updateStep(id, "deliver", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.NOT_DONE,
      revision: rev++,
    });
    coordinator.complete(id, {
      summary: normalized.safeMessage,
      outcome: OUTCOME.NOT_APPLIED,
      requestId: normalized.requestId || null,
      error: {
        safeMessage: normalized.safeMessage,
        requestId: normalized.requestId || null,
        code: normalized.code,
        kind: normalized.kind,
      },
    });
    return { clientOperationId: id, ok: false, error: normalized, payload };
  }

  const jobId = extractJobId(payload);
  // 202 / queued: step confirmed as accepted, not as deliver complete (FA-04).
  coordinator.updateStep(id, "queue", {
    status: STEP_STATUS.COMPLETED,
    detail: t("operation_feedback.send_report.queue_ok"),
    resultRef: jobId ? { job_id: jobId } : undefined,
    revision: rev++,
  });

  if (!jobId) {
    // Legacy sync success without job — treat deliver as not relevant.
    coordinator.updateStep(id, "deliver", {
      status: STEP_STATUS.NOT_RELEVANT,
      detail: t("operation_feedback.send_report.deliver_sync"),
      revision: rev++,
    });
    return completeWithOptionalRefresh({
      coordinator,
      id,
      rev,
      onRefresh,
      t,
      resourceId,
      payload,
    });
  }

  coordinator.updateStep(id, "deliver", {
    status: STEP_STATUS.RUNNING,
    detail: t("operation_feedback.send_report.deliver_queued"),
    revision: rev++,
  });

  let attempts = 0;
  let lastStatusPayload = null;
  while (attempts < maxAttempts) {
    try {
      const statusResponse = await checkStatusFn(jobId);
      lastStatusPayload = statusResponse?.data || {};
      const jobStatus =
        lastStatusPayload.job_status ||
        lastStatusPayload.status ||
        lastStatusPayload.jobStatus;

      if (jobStatus === "completed") {
        coordinator.updateStep(id, "deliver", {
          status: STEP_STATUS.COMPLETED,
          detail: t("operation_feedback.send_report.deliver_ok"),
          resultRef: { job_id: jobId },
          revision: rev++,
        });
        return completeWithOptionalRefresh({
          coordinator,
          id,
          rev,
          onRefresh,
          t,
          resourceId,
          payload: lastStatusPayload,
          onRefreshArg: lastStatusPayload,
        });
      }

      if (jobStatus === "failed") {
        const failMsg =
          lastStatusPayload?.metadata?.error ||
          lastStatusPayload?.error ||
          t("operation_feedback.send_report.deliver_failed");
        const normalized = normalizeError(
          {
            response: {
              status: 422,
              data: { errors: [failMsg], metadata: lastStatusPayload?.metadata },
            },
          },
          { operationKey: SEND_REPORT_OPERATION_KEY }
        );
        coordinator.updateStep(id, "deliver", {
          status: STEP_STATUS.FAILED,
          detail: normalized.safeMessage,
          revision: rev++,
        });
        coordinator.updateStep(id, "refresh", {
          status: STEP_STATUS.NOT_DONE,
          revision: rev++,
        });
        coordinator.complete(id, {
          summary: normalized.safeMessage,
          outcome: OUTCOME.NOT_APPLIED,
          requestId: normalized.requestId || null,
          error: {
            safeMessage: normalized.safeMessage,
            requestId: normalized.requestId || null,
            code: normalized.code,
            kind: normalized.kind,
          },
        });
        return {
          clientOperationId: id,
          ok: false,
          error: normalized,
          payload: lastStatusPayload,
        };
      }

      // queued / processing / unknown — stay RUNNING, do not toast (FE-RP-05).
      coordinator.updateStep(id, "deliver", {
        status: STEP_STATUS.RUNNING,
        detail:
          jobStatus === "processing"
            ? t("operation_feedback.send_report.deliver_processing")
            : t("operation_feedback.send_report.deliver_queued"),
        revision: rev++,
      });
    } catch (pollErr) {
      // Aggregate poll errors silently into step detail — no toast storm.
      const normalized = normalizeError(pollErr, {
        operationKey: SEND_REPORT_OPERATION_KEY,
      });
      if (normalized.kind === "cancelled") {
        coordinator.updateStep(id, "deliver", {
          status: STEP_STATUS.CANCELLED,
          detail: normalized.safeMessage,
          revision: rev++,
        });
        coordinator.updateStep(id, "refresh", {
          status: STEP_STATUS.NOT_DONE,
          revision: rev++,
        });
        coordinator.complete(id, {
          summary: normalized.safeMessage,
          outcome: OUTCOME.UNKNOWN,
          error: {
            safeMessage: normalized.safeMessage,
            kind: normalized.kind,
          },
        });
        return { clientOperationId: id, ok: false, error: normalized };
      }
      coordinator.updateStep(id, "deliver", {
        status: STEP_STATUS.RUNNING,
        detail: t("operation_feedback.send_report.deliver_poll_retry", {
          message: normalized.safeMessage,
        }),
        revision: rev++,
      });
    }

    attempts += 1;
    if (attempts < maxAttempts) {
      await sleepFn(pollIntervalMs);
    }
  }

  coordinator.updateStep(id, "deliver", {
    status: STEP_STATUS.FAILED,
    detail: t("operation_feedback.send_report.deliver_timeout"),
    revision: rev++,
  });
  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_DONE,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.send_report.deliver_timeout"),
    outcome: OUTCOME.UNKNOWN,
  });
  return {
    clientOperationId: id,
    ok: false,
    error: { kind: "timeout" },
    payload: lastStatusPayload,
  };
}

async function completeWithOptionalRefresh({
  coordinator,
  id,
  rev,
  onRefresh,
  t,
  resourceId,
  payload,
  onRefreshArg,
}) {
  const links = resourceId
    ? [
        {
          href: `/dashboard/service/reports/${resourceId}`,
          label: t("operation_feedback.send_report.open_report"),
        },
      ]
    : [];

  if (typeof onRefresh === "function") {
    coordinator.updateStep(id, "refresh", {
      status: STEP_STATUS.RUNNING,
      revision: rev++,
    });
    try {
      await Promise.resolve(onRefresh(onRefreshArg ?? payload));
      coordinator.updateStep(id, "refresh", {
        status: STEP_STATUS.COMPLETED,
        revision: rev++,
      });
      const afterOk = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, false);
      coordinator.complete(id, {
        summary: t("operation_feedback.send_report.success"),
        outcome: afterOk.outcome || OUTCOME.APPLIED,
        links,
      });
      return { clientOperationId: id, ok: true, payload };
    } catch (refreshErr) {
      const normalized = normalizeError(refreshErr, {
        operationKey: SEND_REPORT_OPERATION_KEY,
      });
      const afterFail = outcomeAfterRefreshFailure(STEP_STATUS.COMPLETED, true);
      coordinator.updateStep(id, "refresh", {
        status: afterFail.refreshStatus || STEP_STATUS.FAILED,
        detail: normalized.safeMessage,
        revision: rev++,
      });
      coordinator.complete(id, {
        summary: t(
          afterFail.summaryKey || "operation_feedback.saved_list_refresh_failed"
        ),
        outcome: afterFail.outcome || OUTCOME.PARTIAL,
        links,
      });
      return { clientOperationId: id, ok: true, payload, refreshFailed: true };
    }
  }

  coordinator.updateStep(id, "refresh", {
    status: STEP_STATUS.NOT_RELEVANT,
    revision: rev++,
  });
  coordinator.complete(id, {
    summary: t("operation_feedback.send_report.success"),
    outcome: OUTCOME.APPLIED,
    links,
  });
  return { clientOperationId: id, ok: true, payload };
}
